import {isNumber, isString} from 'util';
import {BehaviorSubject} from 'rxjs';
import {Subject} from 'rxjs';
import {
  Component, DoCheck, ElementRef, Inject, Input,
  OnChanges, OnDestroy, Optional, Self, SimpleChanges,
} from '@angular/core';
import {ControlValueAccessor, FormGroupDirective, NgControl, NgForm} from '@angular/forms';
import {
  CanUpdateErrorState, DateAdapter, ErrorStateMatcher,
  MAT_DATE_FORMATS, MAT_DATE_LOCALE, MAT_INPUT_VALUE_ACCESSOR,
  MatFormFieldControl, MatInputBase, mixinErrorState,
} from '@angular/material';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {MomentDateAdapter} from '@angular/material-moment-adapter';
import {Platform} from '@angular/cdk/platform';
import {OutputMode, YearOrdering} from './date-picker.enum';
import {DatePickerService} from './date-picker.service';
import * as _ from 'lodash';
import * as moment from 'moment';


type ValidValue = Date | string;

export const _DatePickerMixin = mixinErrorState(MatInputBase);

export const MY_FORMATS = {
  parse: {
    dateInput: 'YYYY-MM-DD',
  },
  display: {
    dateInput: 'YYYY-MM-DD',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};


// noinspection JSAnnotator,TypeScriptValidateTypes
/**
 * Credit to Angular 2 Material's MatInput, as most code is copy-pasted faithfully
 */
@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  providers: [
    DatePickerService,
    {
      provide: MatFormFieldControl,
      useExisting: DatePickerComponent,
    },
    {provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE]},
    {provide: MAT_DATE_FORMATS, useValue: MY_FORMATS},
  ],
})
export class DatePickerComponent extends _DatePickerMixin implements MatFormFieldControl<ValidValue>, OnChanges,
  OnDestroy, DoCheck, CanUpdateErrorState, ControlValueAccessor {

  errorState: boolean;
  protected _disabled = false;
  _ariaDescribedby: string;
  protected _id: string;
  protected _uid = _.uniqueId('date-picker-');
  controlType = 'date-picker-';
  focused = false;
  stateChanges: Subject<void> = new Subject<void>();
  @Input() placeholder = '';
  private _readonly = false;
  protected _required = false;
  shouldLabelFloat = true;
  /** An object used to control when error messages are shown. */
  @Input() errorStateMatcher: ErrorStateMatcher;
  _onChange: (value: ValidValue) => void = _.noop;
  /**
   * Implemented as part of MatFormFieldControl.
   * @docs-private
   */
  empty: boolean;
  _value: ValidValue;

  @Input() touchUi = false;
  @Input() startView = 'month';

  @Input() set min(min: string) {
    this.service.min.next(min);
  }

  @Input() set max(max: string) {
    this.service.max.next(max);
  }

  @Input() set yearOrdering(yearOrdering: YearOrdering) {
    this.service.yearOrdering.next(yearOrdering);
  }

  @Input() set mode(mode: OutputMode) {
    this.service.outputMode.next(mode);
    this.showDay = mode !== OutputMode.yearMonth;
    this.showDatePicker = mode !== OutputMode.yearMonth;
  }

  _datePickerValue: moment.Moment;
  showDay = true;
  showDatePicker = true;

  get datePickerValue(): moment.Moment {
    return this._datePickerValue;
  }

  set datePickerValue(datePickerValue: moment.Moment) {
    this._datePickerValue = datePickerValue;
    this.service.input.next(datePickerValue);
  }

  writeValue(obj: Date | string): void {
    this.service.input.next(obj);
  }

  //

  /**
   * Implemented as part of MatFormFieldControl.
   * @docs-private
   */
  @Input()
  get disabled(): boolean {
    if (this.ngControl && this.ngControl.disabled !== null) {
      return this.ngControl.disabled;
    }
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);

    // Browsers may not fire the blur event if the input is disabled too quickly.
    // Reset from here to ensure that the element doesn't become stuck.
    if (this.focused) {
      this.focused = false;
      this.stateChanges.next();
    }
  }

  /**
   * Implemented as part of MatFormFieldControl.
   * @docs-private
   */
  @Input()
  get id(): string {
    return this._id;
  }

  set id(value: string) {
    this._id = value || this._uid;
  }

  /**
   * Implemented as part of MatFormFieldControl.
   * @docs-private
   */
  @Input()
  get required(): boolean {
    return this._required;
  }

  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
  }

  /**
   * Implemented as part of MatFormFieldControl.
   * @docs-private
   */
  @Input()
  get value(): ValidValue {
    return this._value;
  }

  set value(value: ValidValue) {
    if (value !== this.value) {
      this.writeValue(value);
      this.stateChanges.next();
    }
  }

  /** Whether the element is readonly. */
  @Input()
  get readonly(): boolean {
    return this._readonly;
  }

  set readonly(value: boolean) {
    this._readonly = coerceBooleanProperty(value);
  }

  //

  setDisabledState?(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
  }

  setDescribedByIds(ids: string[]): void {
    this._ariaDescribedby = ids.join(' ');
  }

  onContainerClick(event: MouseEvent): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.stateChanges.next();
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
  }

  ngDoCheck(): void {
    // Copied from angualr 2 matInput
    if (this.ngControl) {
      // noinspection TypeScriptUnresolvedFunction
      this.updateErrorState();
    }
  }

  //

  constructor(public service: DatePickerService,
              protected _elementRef: ElementRef,
              protected _platform: Platform,
              /** @docs-private */
              @Optional() @Self() public ngControl: NgControl,
              @Optional() _parentForm: NgForm,
              @Optional() _parentFormGroup: FormGroupDirective,
              _defaultErrorStateMatcher: ErrorStateMatcher,
              @Optional() @Self() @Inject(MAT_INPUT_VALUE_ACCESSOR) inputValueAccessor: any) {

    super(_defaultErrorStateMatcher, _parentForm, _parentFormGroup, ngControl);

    if (this.ngControl) {
      // Note: we provide the value accessor through here, instead of
      // the `providers` to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }

    this.service.outputMoment.subscribe(date => this._datePickerValue = date);
    this.service.output.subscribe(output => {
      this._value = <ValidValue>output;
      this._onChange(<ValidValue>output);
    });
    this.service.hasAnyDatePart.subscribe(isNotEmpty => this.empty = !isNotEmpty);
  }
}
